import { useRef, useState } from 'react';
import { Pagination, Popconfirm } from 'antd';
import dayjs from 'dayjs';

import { useLetterhead } from '@/api/bookings';
import { cashKey } from '@/api/cash';
import { itemUnitsKey } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { outstandingKey } from '@/api/outstanding';
import {
  cancelSale,
  saleInvoicesKey,
  useSaleAccounts,
  useSaleDay,
  useSaleInvoice,
  useSaleInvoices,
  type SaleInvoice,
  type SaleInvoiceSummary,
} from '@/api/saleInvoices';
import { DataView, type DataViewColumn } from '@/components/DataView';
import {
  DocumentHeading,
  DocumentLines,
  DocumentPaper,
  DocumentParties,
  DocumentTop,
  DocumentTotals,
  LineName,
  type DocumentColumn,
} from '@/components/DocumentSheet';
import { enterKeyScope } from '@/components/enterKeyNavigation';
import { hueClasses } from '@/components/hues';
import { IconAction } from '@/components/IconAction';
import {
  Button,
  Card,
  EmptyState,
  Notice,
  PageHeading,
  SectionTitle,
} from '@/components/PageHeading';
import { QuickAmounts, SplitBoxes } from '@/components/SplitBoxes';
import { StatusBadge } from '@/components/StatusBadge';
import { useSplitParts } from '@/components/useSplitParts';
import { paymentMethodLabel } from '@core/bookings/paymentWords';
import { formatDocumentDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import type { SaleInvoiceStatus } from '@core/generated/enums';
import { takeSalePaymentInParts } from '@core/api/saleCommands';
import {
  fillOne,
  filledParts,
  splitTotal,
  splitWords,
  withoutSaved,
  type SplitPart,
} from '@core/cash/split';
import {
  canCancelSale,
  landsIn,
  paidByWords,
  refundOnCancel,
  soldWords,
  saleAsTaxDocument,
  saleStatuses,
  saleStatusLabel,
  saleStatusTone,
} from '@core/sales/sale';
import { can, canAct } from '@core/session/capability';
import type { Session } from '@core/session/types';
import { SheetPrint } from '../print/SheetPrint';
import { invoiceSheet } from '@core/print/sheetHtml';
import { SaleCounter } from './SaleCounter';

const PAGE_SIZE = 25;

type Screen =
  | { kind: 'list' }
  | { kind: 'new' }
  | { kind: 'done'; id: number }
  | { kind: 'view'; id: number }
  | { kind: 'print'; id: number; back: 'done' | 'view' };

const rupees = (value: number) => `₹${formatAmount(value)}`;

const methodWord = (method: SplitPart['method']) =>
  method === 'Cash' ? 'cash' : paymentMethodLabel[method];

/**
 * Shop › Sales (step 5 of `docs/Purchase_Sale_Outstanding_Design.md`; canvas "Sales flow",
 * 2026-10-25) — the day's bills, the counter, Sold, and each bill with its payments. Prices are typed GST-inclusive
 * and the server says what they come to; a garment leaves the stock as sold. Paid now, in part, or
 * later against the customer's outstanding.
 *
 * @param openSaleId Opens straight on one sale — Outstanding's Take payment.
 */
export function SalesPage({
  session,
  openSaleId,
  startNew = false,
}: {
  session: Session;
  openSaleId?: number | undefined;
  /** Opens straight on the counter — Today's New sale (`view: 'new'`). */
  startNew?: boolean;
}) {
  const [screen, setScreen] = useState<Screen>(() =>
    openSaleId !== undefined
      ? { kind: 'view', id: openSaleId }
      : startNew
        ? { kind: 'new' }
        : { kind: 'list' },
  );
  const [day, setDay] = useState(() => dayjs().format('YYYY-MM-DD'));
  const sale = useSaleInvoice(screen.kind === 'list' || screen.kind === 'new' ? null : screen.id);
  const letterhead = useLetterhead(session.branch?.id);

  if (screen.kind === 'new') {
    return (
      <SaleCounter
        session={session}
        onClose={() => setScreen({ kind: 'list' })}
        onSaved={(saved) => setScreen({ kind: 'done', id: saved.id })}
      />
    );
  }

  if (screen.kind === 'print' && sale.data && letterhead.data) {
    const current = sale.data;
    const back = screen.back;
    const shop = letterhead.data;

    return (
      <SheetPrint
        eyebrow={current.customerName ?? 'Sale'}
        title={`Bill ${current.documentNumber}`}
        remember="sale"
        build={(paper, rollLength) =>
          invoiceSheet(
            {
              document: saleAsTaxDocument(current),
              letterhead: shop,
              printedAt: new Date().toISOString(),
            },
            paper,
            rollLength,
          )
        }
        onClose={() => setScreen({ kind: back, id: current.id })}
      />
    );
  }

  if (screen.kind === 'done') {
    return sale.data ? (
      <SaleDone
        session={session}
        sale={sale.data}
        onNext={() => setScreen({ kind: 'new' })}
        onOpen={() => setScreen({ kind: 'view', id: sale.data.id })}
        onList={() => setScreen({ kind: 'list' })}
        onPrint={() => setScreen({ kind: 'print', id: sale.data.id, back: 'done' })}
      />
    ) : null;
  }

  if (screen.kind === 'view') {
    return sale.data ? (
      <SaleView
        session={session}
        sale={sale.data}
        onBack={() => setScreen({ kind: 'list' })}
        onPrint={() => setScreen({ kind: 'print', id: sale.data.id, back: 'view' })}
      />
    ) : null;
  }

  return (
    <SalesToday
      session={session}
      day={day}
      onDay={setDay}
      onNew={() => setScreen({ kind: 'new' })}
      onOpen={(id) => setScreen({ kind: 'view', id })}
    />
  );
}

/** One day's selling — what came in and how, what is left owed, and the bills. */
function SalesToday({
  session,
  day,
  onDay,
  onNew,
  onOpen,
}: {
  session: Session;
  day: string;
  onDay: (day: string) => void;
  onNew: () => void;
  onOpen: (id: number) => void;
}) {
  const figures = useSaleDay(session.branch?.id, day).data;
  const today = dayjs().format('YYYY-MM-DD');
  const shown = dayjs(day);
  const step = (days: number) => onDay(shown.add(days, 'day').format('YYYY-MM-DD'));
  const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;
  const stepper =
    'flex size-10 cursor-pointer items-center justify-center text-[#ffffff] hover:bg-[#ffffff]/10 disabled:cursor-default disabled:opacity-40';

  return (
    <>
      <PageHeading
        eyebrow="Shop · Sales"
        title="Sales"
        subtitle="Garments off the rail and other items over the counter — GST included, paid now or owed."
        actions={
          <>
            <div className="inline-flex h-10 items-center overflow-hidden rounded-lg border border-[#ffffff]/35">
              <button
                type="button"
                aria-label="The day before"
                onClick={() => step(-1)}
                className={stepper}
              >
                <i className="icon icon-chevron-left" aria-hidden="true" />
              </button>
              <span className="px-2 text-[length:var(--scale-list-name)] font-semibold whitespace-nowrap text-[#ffffff]">
                {day === today ? 'Today · ' : ''}
                {shown.format('ddd, D MMM YYYY')}
              </span>
              <button
                type="button"
                aria-label="The day after"
                disabled={day >= today}
                onClick={() => step(1)}
                className={stepper}
              >
                <i className="icon icon-chevron-right" aria-hidden="true" />
              </button>
            </div>
            {canAct(session, 'SalesInvoice:Create') && (
              <Button variant="primary" icon="icon icon-plus" shortcut="create" onClick={onNew}>
                New sale
              </Button>
            )}
          </>
        }
      />

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <DayStat
          label={day === today ? 'Sold today' : 'Sold this day'}
          value={figures ? rupees(figures.total) : '—'}
          hint={
            figures
              ? `${plural(figures.bills, 'bill')} · ${plural(figures.garmentsSold, 'garment')} · GST included`
              : ''
          }
          icon="icon icon-receipt-indian-rupee"
          tile={hueClasses.primary.tile}
        />
        <DayStat
          label="Cash taken"
          value={figures ? rupees(figures.cash) : '—'}
          hint="goes to the till"
          icon="icon icon-banknote"
          tile={hueClasses.success.tile}
        />
        <DayStat
          label="UPI, card & bank"
          value={figures ? rupees(figures.otherMoney) : '—'}
          hint="goes to the bank"
          icon="icon icon-smartphone"
          tile={hueClasses.indigo.tile}
        />
        <DayStat
          label="Left owed"
          value={figures ? rupees(figures.owed) : '—'}
          hint="on this day's bills"
          icon="icon icon-wallet"
          tile={hueClasses.purple.tile}
        />
      </div>

      <SaleList session={session} onOpen={onOpen} day={day} />
    </>
  );
}

/** A figure for the day — muted label, the figure, a coloured tile to the right (`InvoicesPage`'s). */
function DayStat({
  label,
  value,
  hint,
  icon,
  tile,
}: {
  label: string;
  value: string;
  hint: string;
  icon: string;
  tile: string;
}) {
  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-default mb-1 truncate text-[length:var(--scale-list-name)]">{label}</p>
          <h3 className="font-mono-nums mb-0 text-2xl font-bold text-gray-900 max-lg:text-xl">
            {value}
          </h3>
          <p className="text-default mt-0.5 mb-0 truncate text-[length:var(--scale-list-meta)]">
            {hint}
          </p>
        </div>
        <span
          className={`flex size-10 shrink-0 items-center justify-center rounded-md ${tile}`}
          aria-hidden="true"
        >
          <i className={`${icon} text-lg text-white`} />
        </span>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------------------------------------ */

/**
 * The sales register — the day above it by default, every day at a switch. The one place every
 * past sale is listed (owner, 2026-10-25: Rental invoices no longer carries a retail section).
 */
function SaleList({
  session,
  onOpen,
  day,
}: {
  session: Session;
  onOpen: (id: number) => void;
  /** The day the figures above are for. */
  day: string;
}) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<SaleInvoiceStatus | undefined>(undefined);
  const [everyDay, setEveryDay] = useState(false);
  const [page, setPage] = useState(1);
  const sales = useSaleInvoices(session.branch?.id, {
    search,
    status,
    from: everyDay ? undefined : day,
    to: everyDay ? undefined : day,
    page,
    pageSize: PAGE_SIZE,
  });
  const total = sales.data?.total ?? 0;
  const filtered = search.trim() !== '' || status !== undefined;

  const columns: DataViewColumn<SaleInvoiceSummary>[] = [
    {
      title: 'Bill',
      dataIndex: 'documentNumber',
      primary: true,
      render: (_, row) => (
        <div className="whitespace-nowrap">
          <button
            type="button"
            onClick={() => onOpen(row.id)}
            className="text-primary font-mono-nums cursor-pointer text-[length:var(--scale-list-name)] font-semibold hover:underline"
          >
            {row.documentNumber}
          </button>
          <div className="text-default text-[length:var(--scale-list-meta)]">
            {everyDay ? `${formatDocumentDate(row.invoiceDate)} · ` : ''}
            {dayjs(row.createdOn).format('h:mm a')} ·{' '}
            {row.isBillOfSupply ? 'Bill of supply' : 'Tax invoice'}
          </div>
        </div>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      render: (_, row) => (
        <div>
          <div className="text-[length:var(--scale-list-name)] font-semibold text-gray-900">
            {row.customerName}
          </div>
          {row.customerMobile && (
            <div className="text-default font-mono-nums text-[length:var(--scale-list-meta)]">
              {row.customerMobile}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'What was sold',
      dataIndex: 'items',
      hideOnCard: true,
      render: (_, row) => {
        const words = soldWords(row);

        return (
          <div className="max-w-72">
            <div className="text-[length:var(--scale-list-name)] text-gray-900">{words.what}</div>
            {words.codes && (
              <div className="text-default font-mono-nums text-[length:var(--scale-list-meta)]">
                {words.codes}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Paid by',
      dataIndex: 'paidBy',
      hideOnCard: true,
      render: (_, row) => (
        <span className="text-[length:var(--scale-list-name)] text-gray-900">
          {paidByWords(row)}
        </span>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'invoiceTotal',
      align: 'right',
      render: (_, row) => (
        <span className="font-mono-nums text-[length:var(--scale-list-name)] font-semibold whitespace-nowrap text-gray-900">
          {rupees(row.invoiceTotal)}
        </span>
      ),
    },
    {
      title: 'Owed',
      dataIndex: 'balance',
      align: 'right',
      render: (_, row) =>
        row.balance > 0 ? (
          <span className="font-mono-nums text-[length:var(--scale-list-name)] font-semibold whitespace-nowrap text-[color:var(--color-warning)]">
            {rupees(row.balance)}
          </span>
        ) : (
          <span className="text-default">—</span>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_, row) => (
        <StatusBadge tone={saleStatusTone(row.status)}>{saleStatusLabel(row.status)}</StatusBadge>
      ),
    },
    {
      title: '',
      dataIndex: 'id',
      trailing: true,
      render: (_, row) => (
        <IconAction
          icon="icon icon-chevron-right"
          label={`Open ${row.documentNumber}`}
          onClick={() => onOpen(row.id)}
        />
      ),
    },
  ];

  return (
    <Card flush>
      <div className="border-border-color flex flex-wrap items-center justify-between gap-3 border-b p-4">
        <div className="flex flex-wrap items-center gap-2">
          <label className="relative flex items-center">
            <i
              className="icon icon-search text-default pointer-events-none absolute start-3"
              aria-hidden="true"
            />
            <input
              type="search"
              aria-label="Search bills"
              placeholder="Bill number, customer or tag code"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              className="border-border-color h-9 w-72 max-w-full rounded-md border bg-white ps-9 pe-3 text-[length:var(--scale-list-name)] text-gray-900"
            />
          </label>
          <ul
            aria-label="Which days"
            className="border-border-color m-0 flex list-none rounded-lg border p-0.5 ps-0.5"
          >
            {[false, true].map((value) => (
              <li key={String(value)} className="list-none">
                <button
                  type="button"
                  aria-pressed={everyDay === value}
                  onClick={() => {
                    setEveryDay(value);
                    setPage(1);
                  }}
                  className={`cursor-pointer rounded-md px-3 py-1 text-[13px] ${
                    everyDay === value
                      ? 'bg-primary-transparent text-primary font-semibold'
                      : 'text-gray-900 hover:bg-light'
                  }`}
                >
                  {value ? 'All days' : 'This day'}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <ul aria-label="Filter by status" className="m-0 flex list-none flex-wrap gap-1.5 ps-0">
          {[undefined, ...saleStatuses].map((value) => (
            <li key={value ?? 'all'} className="list-none">
              <button
                type="button"
                aria-pressed={status === value}
                onClick={() => {
                  setStatus(value);
                  setPage(1);
                }}
                className={`cursor-pointer rounded-full border px-3 py-1 text-[12px] font-medium ${
                  status === value
                    ? 'border-primary bg-primary text-white'
                    : 'border-border-color text-default bg-white hover:text-gray-900'
                }`}
              >
                {value === undefined ? 'All' : saleStatusLabel(value)}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <DataView<SaleInvoiceSummary>
        columns={columns}
        dataSource={sales.data?.items ?? []}
        loading={sales.isLoading}
        empty={
          <EmptyState
            icon="icon icon-store"
            title={filtered ? 'No bill matches' : everyDay ? 'No sales yet' : 'No sale on this day'}
            hint={
              filtered
                ? 'Clear the search, or pick another status.'
                : 'New sale takes garments off the rail by their tag, and other items by name.'
            }
          />
        }
      />

      {total > 0 && (
        <div className="border-border-color flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
          <span className="text-default text-[length:var(--scale-list-meta)]">
            {total} bill{total === 1 ? '' : 's'} · cancelled bills keep their number in the series
          </span>
          {total > PAGE_SIZE && (
            <Pagination
              current={page}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              showSizeChanger={false}
            />
          )}
        </div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------------------------------------ */

/** Just sold: what happened to the garments and the money, the bill, and the next sale one key away. */
function SaleDone({
  session,
  sale,
  onNext,
  onOpen,
  onList,
  onPrint,
}: {
  session: Session;
  sale: SaleInvoice;
  onNext: () => void;
  onOpen: () => void;
  onList: () => void;
  onPrint: () => void;
}) {
  const saved = sale.lines.reduce((sum, line) => sum + line.discountGiven, 0);
  const walkIn = sale.customerId === null;

  return (
    <>
      <DocumentHeading
        trail="Sales"
        current={sale.documentNumber}
        title={walkIn ? 'Sold' : `Sold to ${sale.customerName}`}
        subtitle={`${formatDocumentDate(sale.invoiceDate)} · ${sale.isBillOfSupply ? 'bill of supply' : 'tax invoice'}`}
        onBack={onList}
        overlap
      />

      <div className="relative mx-auto w-full max-w-2xl">
        <Card>
          <div className="flex items-center gap-4">
            <span
              className="bg-success-transparent flex size-13 shrink-0 items-center justify-center rounded-full text-[color:var(--color-success)]"
              aria-hidden="true"
            >
              <i className="icon icon-check text-2xl" />
            </span>
            <div>
              <p className="font-mono-nums mb-0 font-serif text-3xl leading-tight font-semibold text-gray-900">
                {rupees(sale.invoiceTotal)}
              </p>
              <p
                className={`mb-0 font-semibold ${
                  sale.balance > 0
                    ? 'text-[color:var(--color-warning)]'
                    : 'text-[color:var(--color-success)]'
                }`}
              >
                {sale.balance > 0
                  ? `${rupees(sale.balance)} left owed on ${sale.customerName}'s account`
                  : 'Paid in full'}
                {saved > 0 && (
                  <span className="text-default font-normal"> · saved {rupees(saved)}</span>
                )}
              </p>
            </div>
          </div>

          <ul className="border-border-color mt-5 mb-0 list-none border-t ps-0">
            {sale.lines.map((line) => (
              <li
                key={line.id}
                className="border-border-color flex list-none items-start justify-between gap-3 border-b py-3"
              >
                <div className="min-w-0">
                  <p className="mb-0 font-semibold text-gray-900">{line.description}</p>
                  <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                    {line.kind === 'Garment'
                      ? 'Now sold, off the rental stock'
                      : `Other item · ${line.quantity}`}
                  </p>
                </div>
                <span className="font-mono-nums font-semibold text-gray-900">
                  {rupees(line.lineTotal)}
                </span>
              </li>
            ))}
          </ul>

          {sale.payments.length > 0 && (
            <ul className="mt-4 mb-0 grid list-none grid-cols-1 gap-2.5 ps-0 sm:grid-cols-2">
              {sale.payments.map((payment) => (
                <li key={payment.id} className="bg-light list-none rounded-lg px-3.5 py-3">
                  <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                    {paymentMethodLabel[payment.method]}
                    {payment.reference ? ` · ${payment.reference}` : ''}
                  </p>
                  <p className="font-mono-nums mb-0 font-semibold text-gray-900">
                    {rupees(payment.amount)} to {landsIn(payment)}
                  </p>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            {can(session, 'SalesInvoice:Print') && (
              <Button
                variant="primary"
                icon="icon icon-printer"
                shortcut="print"
                onClick={onPrint}
                className="h-11 flex-1 basis-40 justify-center"
              >
                Print the bill
              </Button>
            )}
            {canAct(session, 'SalesInvoice:Create') && (
              <Button
                icon="icon icon-plus"
                shortcut="create"
                onClick={onNext}
                className="h-11 flex-1 basis-40 justify-center"
              >
                Next sale
              </Button>
            )}
          </div>
          <p className="mt-4 mb-0 text-center text-[length:var(--scale-list-name)]">
            <button
              type="button"
              onClick={onOpen}
              className="text-primary cursor-pointer font-semibold hover:underline"
            >
              Open the bill →
            </button>
            <span className="text-default"> · </span>
            <button
              type="button"
              onClick={onList}
              className="text-primary cursor-pointer hover:underline"
            >
              Back to the day's sales
            </button>
          </p>
        </Card>
      </div>
    </>
  );
}

/** The sale as its paper reads — the line table every sale screen shares. */
function SalePaper({ sale }: { sale: SaleInvoice }) {
  const lineColumns: DocumentColumn<SaleInvoice['lines'][number]>[] = [
    {
      key: 'item',
      title: 'Item',
      render: (line) => (
        <LineName name={line.description} note={line.hsnCode ? `HSN ${line.hsnCode}` : undefined} />
      ),
    },
    { key: 'qty', title: 'Qty', numeric: true, render: (line) => line.quantity },
    {
      key: 'rate',
      title: 'Price, GST in',
      numeric: true,
      render: (line) => formatAmount(line.unitPrice),
    },
    {
      key: 'disc',
      title: 'Off',
      numeric: true,
      render: (line) => (line.discountGiven > 0 ? formatAmount(line.discountGiven) : '—'),
    },
    ...(sale.isBillOfSupply
      ? []
      : [
          {
            key: 'gst',
            title: 'GST',
            numeric: true,
            render: (line: SaleInvoice['lines'][number]) => `${line.ratePct}%`,
          },
        ]),
    {
      key: 'amount',
      title: 'Amount',
      numeric: true,
      render: (line) => <span className="font-semibold">{formatAmount(line.lineTotal)}</span>,
    },
  ];

  return (
    <DocumentPaper>
      <DocumentTop
        issuer={sale.supplierName}
        issuerLines={[
          sale.supplierAddress,
          sale.supplierGstin ? `GSTIN ${sale.supplierGstin}` : null,
        ]}
        title={sale.isBillOfSupply ? 'Bill of supply' : 'Tax invoice'}
        number={sale.documentNumber}
        status={{ label: saleStatusLabel(sale.status), tone: saleStatusTone(sale.status) }}
      />
      <DocumentParties
        heading="Bill to"
        name={sale.customerName}
        lines={[
          sale.customerMobile,
          sale.customerAddress,
          sale.customerGstin ? `GSTIN ${sale.customerGstin}` : null,
        ]}
        facts={[
          { label: 'Sale date', value: formatDocumentDate(sale.invoiceDate) },
          ...(sale.placeOfSupply
            ? [
                {
                  label: 'Place of supply',
                  value: `${sale.placeOfSupply} · ${sale.isInterState ? 'inter-state' : 'intra-state'}`,
                },
              ]
            : []),
          ...(sale.payments.length > 0
            ? [
                {
                  label: 'Paid by',
                  value: [...new Set(sale.payments.map((p) => paymentMethodLabel[p.method]))].join(
                    ', ',
                  ),
                },
              ]
            : []),
        ]}
      />
      <DocumentLines columns={lineColumns} rows={sale.lines} rowKey={(line) => line.id} />
      <DocumentTotals
        rows={[
          ...(sale.discountAmount > 0
            ? [{ label: 'Saved', value: `− ${rupees(sale.discountAmount)}` }]
            : []),
          { label: 'Before tax', value: rupees(sale.taxableValue) },
          ...(sale.isBillOfSupply
            ? []
            : sale.igstAmount > 0
              ? [{ label: 'IGST', value: rupees(sale.igstAmount) }]
              : [
                  { label: 'CGST', value: rupees(sale.cgstAmount) },
                  { label: 'SGST', value: rupees(sale.sgstAmount) },
                ]),
          ...(Math.abs(sale.roundOff) >= 0.005
            ? [{ label: 'Round off', value: rupees(sale.roundOff) }]
            : []),
          { label: 'Total', value: rupees(sale.invoiceTotal), grand: true },
          ...(sale.paid > 0 ? [{ label: 'Paid', value: rupees(sale.paid) }] : []),
          ...(sale.balance > 0 ? [{ label: 'Balance', value: rupees(sale.balance) }] : []),
        ]}
      />
      {sale.notes && (
        <p className="text-default border-border-color mt-6 border-t pt-4 text-[length:var(--scale-list-meta)]">
          {sale.notes}
        </p>
      )}
    </DocumentPaper>
  );
}

/* ------------------------------------------------------------------------------------------------ */

function SaleView({
  session,
  sale,
  onBack,
  onPrint,
}: {
  session: Session;
  sale: SaleInvoice;
  onBack: () => void;
  onPrint: () => void;
}) {
  const [reason, setReason] = useState('');
  const [partly, setPartly] = useState<string | null>(null);
  const amountBox = useRef<HTMLDivElement | null>(null);
  // A box per account (canvas "Money in, by account"); what is still to pay starts in the till's.
  const accounts = useSaleAccounts(session.branch?.id);
  const { parts, change, reset } = useSplitParts(accounts.data, (fresh) =>
    sale.balance > 0 ? fillOne(fresh, sale.balance) : fresh,
  );
  const taking = splitTotal(parts);
  const used = filledParts(parts).length;
  const payable = taking > 0 && taking <= sale.balance;

  const cancel = useApiMutation<SaleInvoice, string>({
    mutationFn: (why) => cancelSale(sale.id, why),
    success: (saved) =>
      `${saved.documentNumber} cancelled${
        saved.lines.some((line) => line.kind === 'Garment')
          ? '; its garments are back on the rail'
          : ''
      }${saved.refunded > 0 ? `; ${rupees(saved.refunded)} refunded` : ''}.`,
    invalidates: [saleInvoicesKey, cashKey, outstandingKey, itemUnitsKey],
  });

  const pay = useApiMutation({
    mutationFn: () => takeSalePaymentInParts(sale.id, parts),
    success: ({ saved, failed }) =>
      failed
        ? `Part taken — ${saved.length} of ${saved.length + 1} parts went through.`
        : `${rupees(taking)} taken on ${sale.documentNumber}.`,
    invalidates: [saleInvoicesKey, cashKey, outstandingKey],
    onSuccess: ({ saved, failed }) => {
      if (!failed) {
        reset();
        setPartly(null);
        return;
      }

      // What went through is emptied, so pressing Take again sends only what is left.
      change((current) => withoutSaved(current, saved));
      const words = splitWords(
        saved.map((each) => each.part),
        methodWord,
        rupees,
        'into',
      );
      setPartly(`${words} — taken. ${failed.part.accountName} was not.`);
    },
  });

  const refund = refundOnCancel(sale);
  const mayTake = sale.balance > 0 && canAct(session, 'SalesInvoice:Create');
  const kind = sale.isBillOfSupply ? 'Bill of supply' : 'Tax invoice';

  return (
    <>
      <DocumentHeading
        trail="Sales"
        current={sale.customerName}
        title={sale.documentNumber}
        status={{ label: saleStatusLabel(sale.status), tone: saleStatusTone(sale.status) }}
        subtitle={[
          kind,
          formatDocumentDate(sale.invoiceDate),
          sale.balance > 0 ? `${rupees(sale.balance)} still to pay` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        onBack={onBack}
        actions={
          <>
            {can(session, 'SalesInvoice:Print') && (
              <Button icon="icon icon-printer" shortcut="print" onClick={onPrint}>
                Print
              </Button>
            )}
            {canCancelSale(sale) && canAct(session, 'SalesInvoice:Edit') && (
              <Popconfirm
                title="Cancel this sale?"
                description={
                  <div className="w-60 pt-1">
                    <p className="text-default mb-1.5 text-[12px]">
                      Its garments go back on the rail. The number stays in the sequence.
                      {refund > 0 &&
                        ` ${rupees(refund)} is refunded out of the cash book today, the way it was paid.`}
                    </p>
                    <input
                      type="text"
                      placeholder="Why?"
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      className="border-border-color h-8 w-full rounded border bg-white px-2 text-[12px] text-gray-900"
                    />
                  </div>
                }
                okText="Cancel sale"
                okButtonProps={{
                  danger: true,
                  loading: cancel.isPending,
                  disabled: reason.trim().length === 0,
                }}
                cancelText="Keep it"
                onConfirm={() => {
                  cancel.mutate(reason.trim());
                  setReason('');
                }}
                placement="bottomRight"
              >
                <Button variant="danger" icon="icon icon-x" disabled={cancel.isPending}>
                  Cancel sale
                </Button>
              </Popconfirm>
            )}
            {mayTake && (
              <Button
                variant="primary"
                icon="icon icon-wallet"
                onClick={() => {
                  amountBox.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  amountBox.current?.querySelector('input')?.focus({ preventScroll: true });
                }}
              >
                Take payment
              </Button>
            )}
          </>
        }
      />

      {sale.cancelledOn && (
        <div className="mb-4">
          <Notice tone="info" title={`Cancelled on ${formatDocumentDate(sale.cancelledOn)}`}>
            {sale.cancelReason}
            {sale.refunded > 0 && ` — ${rupees(sale.refunded)} refunded.`}
          </Notice>
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 xl:col-span-8">
          <SalePaper sale={sale} />
        </div>

        <div className="col-span-12 flex flex-col gap-4 xl:col-span-4">
          <Card>
            <SectionTitle>Payments</SectionTitle>
            {sale.payments.length === 0 ? (
              <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                Nothing paid yet.
              </p>
            ) : (
              <ul className="m-0 flex list-none flex-col ps-0">
                {sale.payments.map((payment) => (
                  <li
                    key={payment.id}
                    className="border-border-color flex list-none items-center justify-between gap-3 border-b py-2.5"
                  >
                    <div>
                      <p className="mb-0 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                        {paymentMethodLabel[payment.method]} · to {landsIn(payment)}
                      </p>
                      <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                        {formatDocumentDate(payment.paidOn)}
                        {payment.reference ? ` · ${payment.reference}` : ''}
                      </p>
                    </div>
                    <span className="font-mono-nums text-end font-semibold text-gray-900">
                      {rupees(payment.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div className="pt-2">
              <Row label="Paid" value={rupees(sale.paid)} />
              {sale.refunded > 0 ? (
                <Row label="Refunded" value={rupees(sale.refunded)} strong />
              ) : (
                <Row label="Still to pay" value={rupees(sale.balance)} strong />
              )}
            </div>
          </Card>

          {mayTake && (
            <Card>
              <div {...enterKeyScope}>
                <SectionTitle>Take a payment · into which account</SectionTitle>
                <div ref={amountBox} className="flex flex-col gap-3">
                  {accounts.isError ? (
                    <Notice tone="danger" title="The accounts could not be read." />
                  ) : (
                    <SplitBoxes
                      parts={parts}
                      onChange={change}
                      direction="into"
                      busy={pay.isPending}
                    />
                  )}
                  <QuickAmounts
                    options={[{ label: `All ${rupees(sale.balance)}`, amount: sale.balance }]}
                    busy={pay.isPending}
                    onPick={(amount) => change((current) => fillOne(current, amount))}
                  />
                  {taking > sale.balance ? (
                    <p className="mb-0 text-[length:var(--scale-list-meta)] text-[color:var(--color-warning)]">
                      That is more than the {rupees(sale.balance)} still to pay.
                    </p>
                  ) : used > 0 ? (
                    <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                      {splitWords(parts, methodWord, rupees, 'into')}.
                    </p>
                  ) : null}
                  {partly ? (
                    <Notice tone="warning" title="Part of it went through">
                      {partly}
                    </Notice>
                  ) : null}
                  <Button
                    variant="primary"
                    icon="icon icon-check"
                    data-enter-key-commit
                    shortcut="save"
                    disabled={!payable || pay.isPending}
                    onClick={() => void pay.mutateAsync().catch(() => undefined)}
                    className="h-11 justify-center"
                  >
                    {pay.isPending
                      ? 'Saving…'
                      : payable
                        ? `Take ${rupees(taking)}${used > 1 ? ` · ${used} payments` : ''}`
                        : 'Take payment'}
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {!sale.cancelledOn && !canCancelSale(sale) && (
            <Card>
              <SectionTitle>Cancel</SectionTitle>
              <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                Money has been taken on this tax invoice, so it can no longer be cancelled.
              </p>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

function Row({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <p className="flex justify-between py-1 text-[13px]">
      <span className="text-default">{label}</span>
      <span
        className={`font-mono-nums text-gray-900 ${strong ? 'text-[15px] font-bold' : 'font-semibold'}`}
      >
        {value}
      </span>
    </p>
  );
}
